import React, { useEffect, useState } from "react";
import {
    Box, Typography, Card, CardContent, Chip, 
    CircularProgress, Stack, Paper, Fade, Avatar,
    Divider, IconButton, Tooltip
} from "@mui/material";
import PublicIcon from '@mui/icons-material/Public';
import HubIcon from '@mui/icons-material/Hub';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

function Otainfo() {
    const [otaData, setOtaData] = useState({ otas: [], roomMappings: [] });
    const [loading, setLoading] = useState(true);
    const [propertyId, setPropertyId] = useState("");

    useEffect(() => {
        const fetchId = async () => {
            const user = auth.currentUser;
            if (user) {
                const snap = await getDoc(doc(db, "authroles", user.uid));
                if (snap.exists()) setPropertyId(snap.data().propertyId);
            }
        };
        fetchId();
    }, []);

    useEffect(() => {
        const fetchOtaInfo = async () => {
            if (!propertyId) return;
            try {
                // 1. Fetch Room Inventory from Firestore for the names
                const inventorySnap = await getDoc(doc(db, "room_inventory", propertyId));
                let roomInventory = [];
                if (inventorySnap.exists()) {
                    roomInventory = inventorySnap.data().rooms || [];
                }

                // 2. Fetch Channel Manager credentials
                const configSnap = await getDoc(doc(db, "channel_manager", propertyId));
                if (configSnap.exists()) {
                    const { hotelCode, authCode } = configSnap.data();
                    
                    // 3. Fetch OTA Mapping from API
                    const res = await fetch('https://navajowhite-wren-495848.hostingersite.com/api/ota-info', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ HotelCode: hotelCode, AuthCode: authCode })
                    });
                    const data = await res.json();

                    if (data.APIResponse?.IsSuccess) {
                        const enrichedMappings = (data.OTARooms || []).map(mapping => {
                            const matchedRoom = roomInventory.find(r => r.RoomId === mapping.RoomId);
                            return {
                                ...mapping,
                                RoomName: matchedRoom ? matchedRoom.RoomName : ""
                            };
                        });

                        setOtaData({
                            otas: data.OTAs || [],
                            roomMappings: enrichedMappings
                        });
                    }
                }
            } catch (err) {
                console.error("OTA Sync Error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchOtaInfo();
    }, [propertyId]);

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.darkGreen, minHeight: "100vh", width: "100%", color: colors.vanilla }}>
            <Box sx={{ flexGrow: 1, p: { xs: 2, md: 1 } }}>
                <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    justifyContent="space-between" 
                    alignItems={{ xs: 'flex-start', sm: 'center' }} 
                    sx={{ mb: 3 }} 
                    spacing={2}
                >
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: colors.vanilla, letterSpacing: '-0.02em' }}>
                            Channel Distribution
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.goldenrod, mt: 0.5, opacity: 0.9 }}>
                            Mapped connections between your inventory and global booking channels.
                        </Typography>
                    </Box>
                    <Chip 
                        icon={<SyncAltIcon sx={{ fontSize: '16px !important', color: colors.darkGreen }} />}
                        label="Live Matrix Active" 
                        sx={{ px: 1.5, py: 2, fontWeight: 700, bgcolor: colors.vanilla, color: colors.darkGreen, border: `1px solid ${colors.goldenrod}` }} 
                    />
                </Stack>

                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 15, gap: 2 }}>
                        <CircularProgress size={40} thickness={4} sx={{ color: colors.gold }} />
                        <Typography sx={{ color: colors.vanilla, fontWeight: 500 }}>Syncing Distribution Data...</Typography>
                    </Box>
                ) : (
                    <div className="container-fluid p-0">
                        <div className="row">
                            {/* Global Channels Summary */}
                            <div className="col-sm-12 mb-4">
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen }}>
                                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                        <Avatar sx={{ bgcolor: colors.vanilla, color: colors.darkGreen, width: 36, height: 36 }}>
                                            <PublicIcon fontSize="small" />
                                        </Avatar>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: colors.vanilla, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Active Global Channels ({otaData.otas.length})
                                        </Typography>
                                    </Stack>
                                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                        {otaData.otas.map((ota) => (
                                            <Chip 
                                                key={ota.OTACode} 
                                                label={ota.OTAName} 
                                                variant="outlined" 
                                                sx={{ bgcolor: colors.darkGreen, fontWeight: 600, borderRadius: '8px', borderColor: colors.goldenrod, color: colors.vanilla }} 
                                            />
                                        ))}
                                    </Box>
                                </Paper>
                            </div>

                            {/* Room Mapping Matrix Header */}
                            <div className="col-sm-12 mb-3">
                                <Typography variant="h6" sx={{ fontWeight: 800, color: colors.vanilla, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <HubIcon sx={{ color: colors.gold }} /> Distribution Matrix
                                </Typography>
                            </div>
                            
                            {otaData.roomMappings.map((mapping, index) => (
                                <div className="col-sm-4 mb-4" key={index}>
                                    <Fade in={true} timeout={300 + index * 100}>
                                        <Card sx={{ 
                                            borderRadius: 4, 
                                            border: `1px solid ${colors.goldenrod}`,
                                            bgcolor: colors.calPolyGreen,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 10px 15px -3px rgba(0, 0, 0, 0.4)` }
                                        }}>
                                            <CardContent sx={{ p: 3, flexGrow: 1 }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: 800, color: colors.goldenrod, textTransform: 'uppercase' }}>
                                                            {mapping.RoomName}
                                                        </Typography>
                                                        <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                                                            <MeetingRoomIcon sx={{ color: colors.gold, fontSize: 18 }} />
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: colors.vanilla }}>
                                                                Room ID: {mapping.RoomId}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                    <Chip 
                                                        label={`Rate Plan: ${mapping.RatePlanId}`} 
                                                        size="small" 
                                                        sx={{ fontWeight: 700, borderRadius: 1.5, bgcolor: colors.darkGreen, color: colors.vanilla, border: `1px solid ${colors.goldenrod}` }} 
                                                    />
                                                </Stack>

                                                <Divider sx={{ my: 2, borderStyle: 'dashed', borderColor: colors.goldenrod, opacity: 0.5 }} />

                                                <Typography variant="caption" sx={{ fontWeight: 700, color: colors.goldenrod, textTransform: 'uppercase', display: 'block', mb: 1.5, opacity: 0.9 }}>
                                                    Connected Channels
                                                </Typography>
                                                
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    {mapping.OTAs.map(ota => (
                                                        <Tooltip title={`Code: ${ota.OTACode}`} key={ota.OTACode} arrow>
                                                            <Chip 
                                                                label={ota.OTAName} 
                                                                size="small" 
                                                                sx={{ 
                                                                    fontWeight: 700, 
                                                                    fontSize: '0.7rem', 
                                                                    bgcolor: colors.darkGreen, 
                                                                    color: colors.vanilla,
                                                                    border: `1px solid ${colors.goldenrod}`
                                                                }} 
                                                            />
                                                        </Tooltip>
                                                    ))}
                                                    {mapping.OTAs.length === 0 && (
                                                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: colors.vanilla, opacity: 0.6 }}>
                                                            No active channels mapped
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </CardContent>
                                            <Box sx={{ px: 3, py: 1.5, bgcolor: colors.darkGreen, borderTop: `1px solid ${colors.goldenrod}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" sx={{ color: colors.vanilla, fontWeight: 600 }}>
                                                    Status: <span style={{ color: colors.gold, fontWeight: 700 }}>Active Sync</span>
                                                </Typography>
                                                <IconButton size="small" sx={{ color: colors.vanilla }}>
                                                    <InfoOutlinedIcon fontSize="inherit" />
                                                </IconButton>
                                            </Box>
                                        </Card>
                                    </Fade>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Box>
        </Box>
    );
}

export default Otainfo;